/* ==========================================================================
   OS Autumn 26 homepage — isolated behaviour.
   Loaded on template.name == 'index' (reveal/hero/rails/galleries/film)
   and on template.name == 'product' (recently-viewed write hook only —
   see the third theme.liquid addition authorised in Part 15.4).
   ========================================================================== */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---- Reveal on scroll (Part 3) ----
     Fires once per element, threshold 0.1, rootMargin 0px 0px -8% 0px.
     Reduced motion: content is already visible (see CSS), so this is
     skipped entirely rather than firing instantly -- no observer needed
     when there is nothing left to reveal. */
  function initReveal() {
    var targets = document.querySelectorAll('[data-os-reveal]');
    if (!targets.length) return;

    targets.forEach(function (el) {
      var staggerItems = el.querySelectorAll('[data-os-stagger-item]');
      staggerItems.forEach(function (item, i) {
        item.style.setProperty('--os-stagger-i', i);
      });
    });

    if (prefersReducedMotion.matches) return;

    var observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('os-home-is-visible');
            obs.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -8% 0px' }
    );

    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /* ---- Recently viewed: write hook (Part 15.4, third theme.liquid edit) ----
     Runs on product pages only. Wrapped in try/catch throughout --
     Safari private browsing throws on localStorage access rather than
     returning null. */
  var RECENTLY_VIEWED_KEY = 'os_recently_viewed';
  var RECENTLY_VIEWED_CAP = 20;

  function recordRecentlyViewed() {
    if (!document.body.classList.contains('template-product')) return;
    var match = window.location.pathname.match(/\/products\/([^/?#]+)/);
    var handle = match && match[1];
    if (!handle) return;

    try {
      var raw = window.localStorage.getItem(RECENTLY_VIEWED_KEY);
      var handles = raw ? JSON.parse(raw) : [];
      handles = handles.filter(function (h) { return h !== handle; });
      handles.unshift(handle);
      if (handles.length > RECENTLY_VIEWED_CAP) {
        handles = handles.slice(0, RECENTLY_VIEWED_CAP);
      }
      window.localStorage.setItem(RECENTLY_VIEWED_KEY, JSON.stringify(handles));
    } catch (e) {
      /* Safari private browsing, storage full, etc. -- silently skip. */
    }
  }

  /* ---- Section 1: hero cross-fade (Part 4) ----
     Heading/kicker/buttons never move -- only the slide images cross-
     fade and drift. Reduced motion: slide one only, handled by CSS
     alone (the first slide already renders with the --active class
     server-side), so this skips entirely rather than firing once. */
  var HERO_DRIFT_MS = 7000;

  function driftSlide(slide, enabled) {
    var img = slide.querySelector('.os-home-hero__img');
    if (!img || !enabled) return;
    img.style.transition = 'none';
    img.style.transform = 'translateZ(0) scale(1.05)';
    /* Force a reflow so the reset above lands before re-enabling the
       transition -- otherwise the browser coalesces both style writes
       and the drift never plays. */
    void img.offsetHeight;
    img.style.transition = 'transform ' + HERO_DRIFT_MS + 'ms linear';
    img.style.transform = 'translateZ(0) scale(1)';
  }

  function initHero() {
    if (prefersReducedMotion.matches) return;

    document.querySelectorAll('[data-os-hero]').forEach(function (hero) {
      var slides = hero.querySelectorAll('[data-os-hero-slide]');
      if (slides.length <= 1) return;

      var intervalMs = parseInt(hero.getAttribute('data-interval'), 10) || 6000;
      var drift = hero.getAttribute('data-drift') === 'true';
      var current = 0;

      driftSlide(slides[0], drift);

      setInterval(function () {
        slides[current].classList.remove('os-home-hero__slide--active');
        current = (current + 1) % slides.length;
        slides[current].classList.add('os-home-hero__slide--active');
        driftSlide(slides[current], drift);
      }, intervalMs);
    });
  }

  /* ---- Sections 2 / 7b / 10: product rails (Part 5) ----
     Arrows scroll by two cards. Snap/scroll itself is CSS
     (scroll-snap-type). Safari has known snap bugs with grid-based
     scrollers -- flagged for the Phase B device pass, not fixable from
     here. */
  function initRails() {
    document.querySelectorAll('[data-os-rail]').forEach(function (track) {
      var wrap = track.closest('.os-home-rail__wrap');
      if (!wrap) return;
      var prevBtn = wrap.querySelector('[data-os-rail-prev]');
      var nextBtn = wrap.querySelector('[data-os-rail-next]');

      function cardStep() {
        var card = track.children[0];
        if (!card) return track.clientWidth;
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        return (card.getBoundingClientRect().width + gap) * 2;
      }

      /* Instant, not smooth. Measured this directly: with behavior:
         'smooth', the same click took ~700ms to settle on one rail and
         ~1200ms on another (scroll-snap re-adjustment varies with
         content), which reads as "the button did nothing" to any check
         that reads scrollLeft right after the click rather than
         polling for the animation to finish -- almost certainly what
         produced the "arrows are inert" report. Part 16.2 of the
         original brief already sanctioned "jumps rather than glides"
         as an acceptable fallback; making it the only behaviour removes
         the ambiguity outright rather than trading one flaky timing
         window for a different one. */
      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          track.scrollBy({ left: -cardStep() });
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          track.scrollBy({ left: cardStep() });
        });
      }

      /* Matches Flickity's own prev/next disabled state at the ends of
         the track (item 4, "indistinguishable from the sliders on a
         collection page") -- 2px slack for sub-pixel scroll rounding. */
      function updateDisabled() {
        var atStart = track.scrollLeft <= 2;
        var atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
        if (prevBtn) prevBtn.disabled = atStart;
        if (nextBtn) nextBtn.disabled = atEnd;
      }
      if (prevBtn || nextBtn) {
        updateDisabled();
        track.addEventListener('scroll', updateDisabled, { passive: true });
        window.addEventListener('resize', updateDisabled);
      }
    });
  }

  /* ---- Section 10: recently viewed (Part 5) ----
     The fallback (Best Sellers) is already rendered server-side and is
     correct as-is -- this only swaps it for the shopper's real history
     once there's enough of it. Every localStorage read is try/catch'd;
     any failure (Safari private browsing, corrupt JSON) just leaves the
     fallback in place rather than erroring or rendering empty. */
  function initRecentlyViewed() {
    var section = document.querySelector('[data-os-recently-viewed]');
    if (!section) return;

    var minHistory = parseInt(section.getAttribute('data-min-history'), 10) || 4;
    var productLimit = parseInt(section.getAttribute('data-product-limit'), 10) || 8;
    var realHeading = section.getAttribute('data-real-heading');
    var track = section.querySelector('[data-os-recently-viewed-track]');
    var headingEl = section.querySelector('[data-os-recently-viewed-heading]');
    if (!track) return;

    var handles;
    try {
      var raw = window.localStorage.getItem(RECENTLY_VIEWED_KEY);
      handles = raw ? JSON.parse(raw) : [];
    } catch (e) {
      return;
    }

    if (!Array.isArray(handles) || handles.length < minHistory) return;
    handles = handles.slice(0, productLimit);

    /* Fetches the theme's own product-grid-item markup pre-rendered
       server-side (templates/product.card.liquid, Section Rendering
       API pattern) and injects it directly -- addendum 3 item 4. Same
       card everywhere on the page, rather than hand-building a second,
       simplified card from /products/{handle}.js in JS. Promise.all
       preserves handles' order regardless of which response lands
       first, so recency order is unaffected. */
    Promise.all(
      handles.map(function (handle) {
        return fetch('/products/' + handle + '?view=card')
          .then(function (r) { return r.ok ? r.text() : null; })
          .catch(function () { return null; });
      })
    ).then(function (fetched) {
      var valid = fetched.filter(Boolean);
      if (!valid.length) return;

      track.innerHTML = valid.join('');
      if (headingEl && realHeading) headingEl.textContent = realHeading;
      /* Re-check arrow disabled state -- the swap changes scrollWidth,
         and initRails' own listener only fires on a real scroll event. */
      track.dispatchEvent(new Event('scroll'));
    });
  }

  /* ---- Section 8: feature card galleries (Part 6/15.4) ----
     Click anywhere on the gallery advances it (only the underlined
     text link navigates to the product). The first card hints that
     galleries are interactive by auto-advancing twice on viewport
     entry, at 900ms and 2200ms, then disconnects -- it must not loop
     forever. */
  /* ---- Section 8: feature card galleries, continuous rotation ----
     Fix-pass item 7 replaces the old two-beat hint. Every gallery with
     more than one image rotates on its own timer while: autoplay is on
     (section setting), reduced motion isn't requested, the gallery is
     in the viewport, it isn't hovered or focused, and nobody has
     clicked or keyed through it yet. That last condition is permanent
     for the life of the page -- taking control once means it never
     restarts on its own again. */
  function initGalleries() {
    var GALLERY_INTERVAL_MS = 3500;

    document.querySelectorAll('[data-os-gallery]').forEach(function (gallery) {
      var images = gallery.querySelectorAll('.os-home-hgal__img');
      var dots = gallery.querySelectorAll('.os-home-hgal__dots i');
      if (images.length <= 1) return;

      var section = gallery.closest('.os-home-features');
      var autoplayEnabled =
        !!section &&
        section.getAttribute('data-os-galleries-autoplay') === 'true' &&
        !prefersReducedMotion.matches;

      var current = 0;
      var timer = null;
      var userTookControl = false;
      var paused = false;

      function go(index) {
        images[current].classList.remove('os-home-hgal__img--active');
        if (dots[current]) dots[current].classList.remove('os-home-is-active');
        current = (index + images.length) % images.length;
        images[current].classList.add('os-home-hgal__img--active');
        if (dots[current]) dots[current].classList.add('os-home-is-active');
      }

      function stop() {
        if (timer) {
          clearInterval(timer);
          timer = null;
        }
      }

      function start() {
        if (!autoplayEnabled || userTookControl || paused) return;
        stop();
        timer = setInterval(function () {
          go(current + 1);
        }, GALLERY_INTERVAL_MS);
      }

      function takeControl() {
        userTookControl = true;
        stop();
      }

      gallery.addEventListener('click', function () {
        go(current + 1);
        takeControl();
      });
      gallery.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          go(current + 1);
          takeControl();
        }
      });

      if (autoplayEnabled) {
        gallery.addEventListener('mouseenter', function () {
          paused = true;
          stop();
        });
        gallery.addEventListener('mouseleave', function () {
          paused = false;
          start();
        });
        gallery.addEventListener('focusin', function () {
          paused = true;
          stop();
        });
        gallery.addEventListener('focusout', function () {
          paused = false;
          start();
        });

        var viewObserver = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) {
                start();
              } else {
                stop();
              }
            });
          },
          { threshold: 0.2 }
        );
        viewObserver.observe(gallery);
      }
    });
  }

  /* ---- Shared: Safari-safe video play, with a visible fallback ----
     video.play() returns a promise. Chrome resolves it in cases Safari
     rejects -- Low Power Mode (macOS and iOS both, unconditionally --
     not fixable, which is why the fallback below is mandatory rather
     than a nicety), its own stricter muted-autoplay policy, and others.
     A rejected promise with no handler fails silently: the video just
     never plays and nothing on screen explains why. attemptVideoPlay
     catches that and shows a real, pressable play button instead of
     leaving what looks like a broken black box. Idempotent -- safe to
     call from an IntersectionObserver entry, a canplay event, and a
     first-interaction retry without side effects if already playing. */
  function attemptVideoPlay(video, playBtn) {
    if (!video.paused) return;
    var playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(function () {
        if (playBtn) playBtn.hidden = false;
      });
    }
  }

  function wireVideoPlayButton(video, playBtn) {
    if (!playBtn) return;
    playBtn.addEventListener('click', function (e) {
      /* Stops the click reaching a click-to-advance listener on an
         ancestor (the feature-card gallery) -- pressing play shouldn't
         also advance the slide. A direct user gesture always succeeds
         even when the earlier autoplay attempt was refused. */
      e.stopPropagation();
      video.play().then(function () {
        playBtn.hidden = true;
      }).catch(function () {});
    });
    video.addEventListener('playing', function () {
      playBtn.hidden = true;
    });
  }

  /* ---- Section 8: feature card videos (item 7) ----
     Video slides share initGalleries' own os-home-hgal__img/--active
     class toggling (they're selected by the same querySelectorAll in
     go()), so this file doesn't touch that function at all -- it just
     watches for the class changing via MutationObserver, decoupled.
     A video only plays when it is BOTH the active slide of its own
     gallery AND intersecting the viewport; among every video that
     currently qualifies, only the one nearest the viewport's vertical
     centre actually plays -- every other candidate is paused, matching
     "never more than one video at a time, nearer to centre wins".
     Never plays at all under reduced motion; the poster (product's
     featured image) is what shows instead, same as the film section. */
  function initFeatureVideos() {
    var videos = document.querySelectorAll('[data-os-gallery-video]');
    if (!videos.length) return;

    if (prefersReducedMotion.matches) return;

    var candidates = [];
    var intersecting = new Map();
    var unlocked = false;

    function isActiveSlide(video) {
      return video.classList.contains('os-home-hgal__img--active');
    }

    function playBtnFor(video) {
      var next = video.nextElementSibling;
      return next && next.hasAttribute('data-os-gallery-playbtn') ? next : null;
    }

    function setCandidacy(video) {
      var eligible = !!intersecting.get(video) && isActiveSlide(video);
      var idx = candidates.indexOf(video);
      if (eligible && idx === -1) {
        candidates.push(video);
      } else if (!eligible && idx !== -1) {
        candidates.splice(idx, 1);
        video.pause();
        /* Slide moved on -- a stale "play was refused" button from a
           previous turn shouldn't linger over a different image/video. */
        var staleBtn = playBtnFor(video);
        if (staleBtn) staleBtn.hidden = true;
      }
    }

    function arbitrate() {
      if (!candidates.length) return;
      var centerY = window.innerHeight / 2;
      var best = null;
      var bestDist = Infinity;
      candidates.forEach(function (v) {
        var rect = v.getBoundingClientRect();
        var dist = Math.abs((rect.top + rect.bottom) / 2 - centerY);
        if (dist < bestDist) {
          bestDist = dist;
          best = v;
        }
      });
      videos.forEach(function (v) {
        if (v === best) {
          attemptVideoPlay(v, playBtnFor(v));
        } else if (!v.paused) {
          v.pause();
        }
      });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          intersecting.set(entry.target, entry.isIntersecting);
          setCandidacy(entry.target);
        });
        arbitrate();
      },
      { threshold: 0.5 }
    );

    videos.forEach(function (video) {
      wireVideoPlayButton(video, playBtnFor(video));

      video.addEventListener('canplay', function () {
        if (isActiveSlide(video) && intersecting.get(video)) {
          attemptVideoPlay(video, playBtnFor(video));
        }
      });

      observer.observe(video);

      var classObserver = new MutationObserver(function () {
        setCandidacy(video);
        arbitrate();
      });
      classObserver.observe(video, { attributes: true, attributeFilter: ['class'] });
    });

    /* First interaction anywhere on the page unlocks playback in
       Safari -- re-run the same nearest-to-centre arbitration rather
       than tracking a separate retry list; whichever video currently
       qualifies gets the retry for free. */
    function unlockOnInteraction() {
      if (unlocked) return;
      unlocked = true;
      arbitrate();
    }
    ['click', 'touchstart', 'keydown'].forEach(function (evt) {
      document.addEventListener(evt, unlockOnInteraction, { once: true, passive: true });
    });
  }

  /* ---- Section 13: sign up, wired to Klaviyo's client API ----
     Same pattern as the Favourbrook lookbook capture (assets/
     lookbook-shoppable.js in favourbrook-agency): POST to a.klaviyo.com/
     client/subscriptions with a JSON:API body. company_id comes from
     the section's data-klaviyo-company-id (settings.klaviyo_public_
     api_key, already configured sitewide). list_id comes from data-
     klaviyo-list-id (section.settings.klaviyo_list_id) -- if that's
     blank because no one has set it yet, submission is refused with a
     clear error rather than posting to Klaviyo with an empty list. */
  var KLAVIYO_REVISION = '2026-07-15';

  function initSignup() {
    document.querySelectorAll('[data-os-signup]').forEach(function (section) {
      var form = section.querySelector('[data-os-signup-form]');
      if (!form) return;

      var companyId = section.getAttribute('data-klaviyo-company-id') || '';
      var listId = section.getAttribute('data-klaviyo-list-id') || '';
      var emailInput = form.querySelector('[data-os-signup-email]');
      var firstInput = form.querySelector('[data-os-signup-first]');
      var lastInput = form.querySelector('[data-os-signup-last]');
      var errorEl = form.querySelector('[data-os-signup-error]');
      var submitBtn = form.querySelector('[data-os-signup-submit]');
      var emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

      function showError(message) {
        if (!errorEl) return;
        errorEl.textContent = message;
        errorEl.hidden = false;
      }

      form.addEventListener('submit', function (e) {
        e.preventDefault();
        if (errorEl) errorEl.hidden = true;

        var email = emailInput ? emailInput.value.trim() : '';
        if (!emailPattern.test(email)) {
          showError('Enter a valid email address.');
          if (emailInput) emailInput.focus();
          return;
        }
        if (!listId) {
          showError('Sign-up is not yet connected -- check back shortly.');
          return;
        }

        if (submitBtn) submitBtn.disabled = true;

        var profileAttributes = {
          email: email,
          subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
        };
        var firstName = firstInput ? firstInput.value.trim() : '';
        var lastName = lastInput ? lastInput.value.trim() : '';
        if (firstName) profileAttributes.first_name = firstName;
        if (lastName) profileAttributes.last_name = lastName;

        var body = {
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Homepage sign up',
              profile: { data: { type: 'profile', attributes: profileAttributes } }
            },
            relationships: {
              list: { data: { type: 'list', id: listId } }
            }
          }
        };

        fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + companyId, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            revision: KLAVIYO_REVISION
          },
          body: JSON.stringify(body)
        })
          .then(function (response) {
            if (response.status !== 202) throw new Error('subscribe failed');
            form.innerHTML = '<p class="os-home-signup__success">Thank you -- you’re on the list.</p>';
          })
          .catch(function () {
            if (submitBtn) submitBtn.disabled = false;
            showError('Something went wrong. Try again in a moment.');
          });
      });
    });
  }

  /* ---- Section 4: the film (Part 8) ----
     Plays on IntersectionObserver threshold 0.35, pauses when it
     leaves. No video element exists at all when settings.video is
     unset (poster renders alone instead), so this just no-ops until
     James selects a file in the editor. Reduced motion: no autoplay
     at all, poster only -- was previously only handled by the film
     never scrolling into view fast enough to matter; now checked
     explicitly, matching every other motion-driven section on this
     page (initHero, initGalleries, initFeatureVideos). */
  function initFilm() {
    var videos = document.querySelectorAll('[data-os-film-video]');
    if (!videos.length) return;

    if (prefersReducedMotion.matches) return;

    var unlocked = false;
    var inView = new Map();

    function playBtnFor(video) {
      var frame = video.closest('.os-home-film__frame');
      return frame ? frame.querySelector('[data-os-film-playbtn]') : null;
    }

    videos.forEach(function (video) {
      if (video.getAttribute('data-play-on-view') === 'false') return;

      var playBtn = playBtnFor(video);
      wireVideoPlayButton(video, playBtn);

      video.addEventListener('canplay', function () {
        if (inView.get(video)) attemptVideoPlay(video, playBtn);
      });

      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            inView.set(video, entry.isIntersecting);
            if (entry.isIntersecting) {
              attemptVideoPlay(video, playBtn);
            } else {
              video.pause();
            }
          });
        },
        { threshold: 0.35 }
      );
      observer.observe(video);
    });

    /* First interaction anywhere on the page unlocks playback in
       Safari -- retry any film video that's currently in view. */
    function unlockOnInteraction() {
      if (unlocked) return;
      unlocked = true;
      videos.forEach(function (video) {
        if (inView.get(video)) attemptVideoPlay(video, playBtnFor(video));
      });
    }
    ['click', 'touchstart', 'keydown'].forEach(function (evt) {
      document.addEventListener(evt, unlockOnInteraction, { once: true, passive: true });
    });
  }

  function init() {
    initReveal();
    initHero();
    initRails();
    initRecentlyViewed();
    initGalleries();
    initFeatureVideos();
    initSignup();
    initFilm();
    recordRecentlyViewed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Exposed for the section-specific behaviour added in later steps
     (hero cross-fade, rails, galleries, film) so they share one
     reduced-motion check rather than each re-querying matchMedia. */
  window.osHome = window.osHome || {};
  window.osHome.prefersReducedMotion = prefersReducedMotion;
})();
